import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRaces } from '@/hooks/useRaces';
import { useDiscounts } from '@/hooks/useDiscounts';
import { useRaceTypes } from '@/hooks/useRaceTypes';
import { SearchInput, StatusBadge, ActionButtons, editAction, deleteAction } from '@/components/shared';
import { useDiscountsActions } from './useDiscountsActions';
import DiscountsManagerView from './discountsmanager.view';

interface DiscountsManagerOwnProps {
  raceId?: string;
}

const DiscountsManager: React.FC<DiscountsManagerOwnProps> = ({ raceId: raceIdProp }) => {
  const { user, isOrganizer } = useAuth();
  const [selectedRaceIdState, setSelectedRaceIdState] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const isEmbedded = !!raceIdProp;
  const selectedRaceId = isEmbedded ? raceIdProp : selectedRaceIdState;

  const raceParams = isOrganizer() && user?.organizerId ? { organizerId: user.organizerId } : {};
  const { data: races = [] } = useRaces(isEmbedded ? undefined : raceParams);
  const { data: discountCodes = [], isLoading: loading } = useDiscounts(selectedRaceId);
  const { data: raceTypes = [] } = useRaceTypes(selectedRaceId);

  useEffect(() => {
    if (!isEmbedded && races.length > 0 && !selectedRaceIdState) {
      setSelectedRaceIdState(races[0].id);
    }
  }, [races, selectedRaceIdState, isEmbedded]);

  const actions = useDiscountsActions(selectedRaceId);

  const getDiscountScopeLabel = (discount: any) => {
    if (!discount.raceTypeId) {
      return 'Цела трка';
    }

    const scopedRaceType = raceTypes.find((raceType: any) => String(raceType.id) === String(discount.raceTypeId));
    return scopedRaceType ? scopedRaceType.name : `Тип на трка #${discount.raceTypeId}`;
  };

  const filteredDiscounts = discountCodes.filter(
    (dc: any) =>
      dc.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dc.discountPercentage?.toString().includes(searchQuery) ||
      dc.discountAmount?.toString().includes(searchQuery) ||
      getDiscountScopeLabel(dc).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isValidDiscount = (discount: any) => {
    const now = new Date();
    const validFrom = new Date(discount.validFrom);
    const validTo = new Date(discount.validTo);
    return (
      discount.active &&
      now >= validFrom &&
      now <= validTo &&
      discount.currentUses < discount.maxUses
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  const discountColumns = [
    { key: 'code', label: 'Код', cellClassName: 'whitespace-nowrap', render: (v: string) => <span className="font-mono font-semibold text-foreground">{v}</span> },
    { key: 'raceTypeId', label: 'Опсег', cellClassName: 'whitespace-nowrap text-muted-foreground', render: (_: any, d: any) => getDiscountScopeLabel(d) },
    { key: 'discountPercentage', label: 'Попуст', cellClassName: 'whitespace-nowrap text-muted-foreground', render: (v: any, d: any) => v ? `${v}%` : `${d.discountAmount} МКД` },
    { key: 'validFrom', label: 'Важи од/до', cellClassName: 'whitespace-nowrap text-muted-foreground text-sm', render: (v: string, d: any) => `${formatDate(v)} - ${formatDate(d.validTo)}` },
    { key: 'currentUses', label: 'Користења', cellClassName: 'whitespace-nowrap text-muted-foreground', render: (v: number, d: any) => `${v} / ${d.maxUses}` },
    { key: 'active', label: 'Статус', cellClassName: 'whitespace-nowrap', render: (_: any, d: any) => <StatusBadge label={isValidDiscount(d) ? 'Активен' : 'Неактивен'} variant={isValidDiscount(d) ? 'success' : 'error'} /> },
    { key: '_actions', label: 'Акции', className: 'text-right', cellClassName: 'whitespace-nowrap text-right', render: (_: any, d: any) => (
      <ActionButtons actions={[editAction(() => actions.handleEdit(d)), deleteAction(() => actions.handleDelete(d.id))]} />
    )},
  ];

  return (
    <DiscountsManagerView
      races={isEmbedded ? [] : races}
      raceTypes={raceTypes}
      selectedRaceId={selectedRaceId}
      setSelectedRaceId={isEmbedded ? () => {} : setSelectedRaceIdState}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      loading={loading}
      filteredDiscounts={filteredDiscounts}
      discountColumns={discountColumns}
      isDialogOpen={actions.isDialogOpen}
      setIsDialogOpen={actions.setIsDialogOpen}
      editingDiscount={actions.editingDiscount}
      setEditingDiscount={actions.setEditingDiscount}
      formData={actions.formData}
      setFormData={actions.setFormData}
      handleSubmit={actions.handleSubmit}
      deleteTarget={actions.deleteTarget}
      setDeleteTarget={actions.setDeleteTarget}
      confirmDelete={actions.confirmDelete}
      embedded={isEmbedded}
    />
  );
};

export default DiscountsManager;
